import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal, OnInit, PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TrimWhitespaceDirective } from '../../../core/directives/trim.directive';
import { trimFormGroup } from '../../../core/utils/form.utils';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'app-verify-email',
  templateUrl: './verify-email.html',
  styleUrl: './verify-email.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, TrimWhitespaceDirective],
})
export class VerifyEmail implements OnInit {
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);

  readonly isLoading = signal(false);
  readonly isVerified = signal(false);
  readonly isFailed = signal(false);
  readonly countdown = signal(3);
  readonly verificationStep = signal<'auto' | 'manual'>('auto');
  readonly pendingEmail = signal<string | null>(null);

  readonly resendForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  async ngOnInit(): Promise<void> {
    // Only run verification logic in the browser (not during SSR)
    if (!isPlatformBrowser(this.platformId)) return;

    // Use raw window.location.search to preserve encoded chars like %2f, %2b, %3d
    const rawSearch = window.location.search;
    let token: string | null = null;

    if (rawSearch) {
      const match = rawSearch.match(/[?&]token=([^&]*)/);
      if (match) {
        token = decodeURIComponent(match[1]);
      }
    }

    // Fallback to Angular's router query params
    if (!token) {
      token = this.route.snapshot.queryParamMap.get('token');
    }

    if (token) {
      this.isLoading.set(true);
      await this.autoVerify(token);
    } else {
      this.verificationStep.set('manual');
    }
  }

  private startCountdown(redirectTo: string): void {
    let count = 3;
    this.countdown.set(count);
    const interval = setInterval(() => {
      count--;
      this.countdown.set(count);
      this.cdr.markForCheck();
      if (count <= 0) {
        clearInterval(interval);
        this.router.navigate([redirectTo]);
      }
    }, 1000);
  }

  private async autoVerify(token: string): Promise<void> {
    const resp = await this.authService.verifyEmail(token);

    if (resp.status) {
      this.isVerified.set(true);
      this.toastService.success(resp.message);
      this.startCountdown('/create-family-space');
    } else {
      this.isFailed.set(true);
      this.toastService.error(resp.message);
      this.startCountdown('/login');
    }

    this.isLoading.set(false);
    this.cdr.markForCheck();
  }

  async onResendVerification(): Promise<void> {
    trimFormGroup(this.resendForm);
    if (this.resendForm.invalid) {
      this.resendForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    const { email } = this.resendForm.value;
    const resp = await this.authService.resendVerification(email!);

    if (resp.status) {
      this.toastService.success(resp.message);
      this.pendingEmail.set(email!);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('pendingVerificationEmail', email!);
      }
    } else {
      this.toastService.error(resp.message);
    }

    this.isLoading.set(false);
  }

  get f() { return this.resendForm.controls; }
}
